import { useNavigate, useLocation } from "react-router-dom";
import {
  getHospitalList,
  updatePartnerActiveStatus,
} from "../../../../Service/RM";
import { useState, useMemo, useEffect } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { VscEye } from "react-icons/vsc";
import { IoIosAddCircleOutline } from "react-icons/io";
import Loader from "../../../../reusableUI/loader";
import { formatPhoneNumber } from "../../../../utils/Functions";
import { capitalizeFirstLetter } from "../../../../utils/Functions";
import { CommonImages } from "../../../../reusableUI/images";
import ReusableTable from "../../../../reusableUI/table";
import Page from "../../../../reusableUI/page";
import Performence from "./performence";
import CommonDropdown from "../../../../reusableUI/dropdown";
import {
  MdOutlineFilterList,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
} from "react-icons/md";
import { GrPowerReset } from "react-icons/gr";
import { toast } from "react-toastify";

const PARTNER_TYPE_LABELS = {
  hospital: "Hospital",
  agency: "Agency",
  company: "Company",
  individual: "Individual",
  laboratory: "Lab",
};

const PARTNER_AVATAR_COLORS = [
  "#BFDBFE",
  "#DDD6FE",
  "#FBCFE8",
  "#FECDD3",
  "#FED7AA",
  "#BBF7D0",
  "#A5F3FC",
  "#FEF3C7",
];

const getPartnerInitials = (name) => {
  const words = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) return "?";

  return words
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();
};

const getPartnerAvatarColor = (name) => {
  const normalizedName = String(name || "?")
    .trim()
    .toLowerCase();
  let hash = 0;

  for (let index = 0; index < normalizedName.length; index += 1) {
    hash = (hash * 31 + normalizedName.charCodeAt(index)) >>> 0;
  }

  return PARTNER_AVATAR_COLORS[hash % PARTNER_AVATAR_COLORS.length];
};

const normalizePartnerType = (type) => {
  const normalizedType = String(type || "")
    .trim()
    .toLowerCase();
  return normalizedType === "lab" ? "laboratory" : normalizedType;
};

const getPartnerTypeLabel = (type) => {
  const normalizedType = normalizePartnerType(type);
  return (
    PARTNER_TYPE_LABELS[normalizedType] || capitalizeFirstLetter(normalizedType)
  );
};

const Hospital = ({ searchTerm }) => {
  const [loading, setLoading] = useState(true);
  const [paginationPage, setPaginationPage] = useState(0);
  const [userList, setUserList] = useState([]);
  const [noUsersFound, setNoUsersFound] = useState(false);
  const [selectedTab, setSelectedTab] = useState("partner");
  const [selectedPageCount, setSelectedPageCount] = useState(() => {
    const savedPageCount = localStorage.getItem("partnerPageCount");
    return savedPageCount ? parseInt(savedPageCount, 10) : 50;
  });

  const [sortBy, setSortBy] = useState(() => {
    const savedSortBy = localStorage.getItem("partnerSortBy");
    return savedSortBy ? JSON.parse(savedSortBy) : [];
  });
  const defaultFilters = useMemo(
    () => ({
      partnerType: [],
      city: [],
    }),
    [],
  );
  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem("partnerFilters");
    if (!savedFilters) return defaultFilters;

    try {
      return { ...defaultFilters, ...JSON.parse(savedFilters) };
    } catch {
      return defaultFilters;
    }
  });
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showResetPopup, setShowResetPopup] = useState(false);
  const [togglingPartnerId, setTogglingPartnerId] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    getUserList();
  }, []);

  useEffect(() => {
    if (location.state?.currentPage !== undefined) {
      setPaginationPage(location.state.currentPage);
    }
  }, [location.state]);

  useEffect(() => {
    localStorage.setItem("partnerFilters", JSON.stringify(filters));
    const applied = Object.values(filters).some(
      (value) => Array.isArray(value) && value.length > 0,
    );
    setIsFilterApplied(applied);
  }, [filters]);

  const getUserList = async () => {
    setLoading(true); // Start loading
    try {
      const result = await getHospitalList();
      if (!result.error && Array.isArray(result.data)) {
        setUserList(result.data);
      } else {
        console.error("Failed to fetch hospital list");
        setUserList([]); // Set empty array on error
      }
    } catch (error) {
      console.error("Error fetching hospital list:", error);
      setUserList([]); // Set empty array on error
    } finally {
      setLoading(false); // Always stop loading
    }
  };

  const AddHospital = () => {
    localStorage.setItem("profilePage", "hospital");
    navigate("/admin/partnerEdit");
  };

  const pageCount = [
    { label: "25", value: 25 },
    { label: "50", value: 50 },
    { label: "75", value: 75 },
    { label: "100", value: 100 },
  ];

  const handleChangePageCount = (event) => {
    const newValue = Number(event.target.value);
    setSelectedPageCount(newValue);
    setPaginationPage(0);
    localStorage.setItem("partnerPageCount", String(newValue));
  };

  const partnerTypeOptions = useMemo(() => {
    const counts = new Map(
      Object.entries(PARTNER_TYPE_LABELS).map(([value, label]) => [
        value,
        { value, label, count: 0 },
      ]),
    );

    userList.forEach((item) => {
      const type = item?.providerType?.trim();
      if (!type) return;
      const key = normalizePartnerType(type);
      const existing = counts.get(key);
      counts.set(key, {
        value: key,
        label: getPartnerTypeLabel(type),
        count: existing ? existing.count + 1 : 1,
      });
    });

    return Array.from(counts.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [userList]);

  const cityOptions = useMemo(() => {
    const counts = new Map();

    userList.forEach((item) => {
      const city = item?.addressInfo?.city?.trim();
      if (!city) return;
      const key = city.toLowerCase();
      const existing = counts.get(key);
      counts.set(key, {
        value: key,
        label: capitalizeFirstLetter(city),
        count: existing ? existing.count + 1 : 1,
      });
    });

    return Array.from(counts.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [userList]);

  const toggleFilterValue = (key, value) => {
    setFilters((prev) => {
      const current = prev[key] || [];
      const nextValues = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];

      return {
        ...prev,
        [key]: nextValues,
      };
    });
  };

  const resetFilters = () => {
    if (!isFilterApplied) return;

    setFilters(defaultFilters);
    setDropdownOpen(false);
    setShowResetPopup(true);
    setTimeout(() => setShowResetPopup(false), 1500);
  };

  const handlePartnerActiveToggle = async (partnerId, currentActive) => {
    if (!partnerId || togglingPartnerId) return;

    const nextActive = !currentActive;
    setTogglingPartnerId(partnerId);

    try {
      const result = await updatePartnerActiveStatus(partnerId, nextActive);

      if (!result?.error) {
        setUserList((prev) =>
          prev.map((partner) =>
            partner?._id === partnerId
              ? { ...partner, active: nextActive }
              : partner,
          ),
        );
        toast.success(
          `Partner ${nextActive ? "activated" : "deactivated"} successfully`,
        );
      } else {
        console.error(
          "Failed to update partner active status:",
          result?.message,
        );
        toast.error(result?.message || "Failed to update partner status");
      }
    } catch (error) {
      console.error("Error updating partner active status:", error);
      toast.error("Failed to update partner status");
    } finally {
      setTogglingPartnerId(null);
    }
  };

  const getColumns = () => [
    {
      Header: "Partner Name",
      accessor: "hospitalName",
      id: "hospitalName",
      width: 200,
      minWidth: 180,
      maxWidth: 250,
      disableSortBy: false,
      sortType: (rowA, rowB) => {
        const nameA = (rowA.original?.basicInfo?.name || "").toLowerCase();
        const nameB = (rowB.original?.basicInfo?.name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      },
      headerStyle: {
        textAlign: "left",
        padding: "8px",
      },
      style: {
        textAlign: "left",
        padding: "8px",
        whiteSpace: "nowrap",
        overflow: "visible",
      },
      Cell: ({ row: { original } }) => {
        const partnerName = original?.basicInfo?.name || "";

        return (
          <div className="flex flex-row items-center gap-1.5 whitespace-nowrap ml-2">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-slate-800"
              style={{ backgroundColor: getPartnerAvatarColor(partnerName) }}
              role="img"
              aria-label={`${partnerName || "Partner"} initials`}
            >
              {getPartnerInitials(partnerName)}
            </div>
            <span
              className="text-blue-600 text-[12px] font-normal"
              title={partnerName || "-"}
            >
              <p className="max-w-[130px] truncate">{partnerName || "-"}</p>
            </span>
          </div>
        );
      },
    },
    {
      Header: "Provider Type",
      accessor: "providerType",
      id: "providerType",
      width: 120,
      minWidth: 110,
      maxWidth: 140,
      disableSortBy: false,
      sortType: "alphanumeric",
      headerStyle: {
        textAlign: "center",
        padding: "8px",
      },
      style: {
        textAlign: "center",
        padding: "8px",
      },
      Cell: ({ row: { original } }) => (
        <span className="text-blue-600 text-[12px]">
          {getPartnerTypeLabel(original?.providerType) || "-"}
        </span>
      ),
    },

    {
      Header: "Location",
      accessor: "location",
      id: "location",
      width: 120,
      minWidth: 100,
      maxWidth: 140,
      disableSortBy: false,
      sortType: (rowA, rowB) => {
        const locA = (rowA.original?.addressInfo?.city || "").toLowerCase();
        const locB = (rowB.original?.addressInfo?.city || "").toLowerCase();
        if (!locA && locB) return 1;
        if (locA && !locB) return -1;
        return locA.localeCompare(locB);
      },
      headerStyle: {
        textAlign: "center",
        padding: "8px",
      },
      style: {
        textAlign: "center",
        padding: "8px",
      },
      Cell: ({ row: { original } }) => (
        <span className="text-[12px]">
          {capitalizeFirstLetter(original?.addressInfo?.city || "-")}
        </span>
      ),
    },
    {
      Header: "Contact Person",
      accessor: "contactPerson",
      id: "contactPerson",
      width: 160,
      minWidth: 140,
      maxWidth: 200,
      disableSortBy: false,
      sortType: (rowA, rowB) => {
        const nameA = (
          rowA.original?.contactInfo?.[0]?.name || ""
        ).toLowerCase();
        const nameB = (
          rowB.original?.contactInfo?.[0]?.name || ""
        ).toLowerCase();
        return nameA.localeCompare(nameB);
      },
      headerStyle: {
        textAlign: "left",
        padding: "8px",
      },
      style: {
        textAlign: "left",
        padding: "8px",
      },
      Cell: ({ row: { original } }) => (
        <span
          className="text-[12px] truncate max-w-[100px] inline-block"
          title={original?.contactInfo?.[0]?.name || "-"}
        >
          {original?.contactInfo?.[0]?.name || "-"}
        </span>
      ),
    },
    {
      Header: "Contact Number",
      accessor: "contactNumber",
      id: "contactNumber",
      width: 140,
      minWidth: 130,
      maxWidth: 160,
      disableSortBy: false,
      sortType: "alphanumeric",
      headerStyle: {
        textAlign: "center",
        padding: "6px",
      },
      style: {
        textAlign: "center",
        padding: "8px",
      },
      Cell: ({ row: { original } }) => {
        const rawPhone = original?.contactInfo?.[0]?.phone;
        const phoneWithCountryCode = rawPhone ? Number(rawPhone) : null;

        return (
          <span className="text-blue-600 text-[12px] whitespace-nowrap">
            {phoneWithCountryCode
              ? formatPhoneNumber(phoneWithCountryCode)
              : "-"}
          </span>
        );
      },
    },
    {
      Header: "Email Id",
      accessor: "email",
      id: "email",
      width: 200,
      minWidth: 180,
      maxWidth: 250,
      disableSortBy: false,
      sortType: "alphanumeric",
      headerStyle: {
        textAlign: "left",
        padding: "8px",
      },
      style: {
        textAlign: "left",
        padding: "8px",
      },
      Cell: ({ row: { original } }) => (
        <span
          className="text-[12px]  max-w-[120px] inline-block"
          title={original?.contactInfo?.[0]?.email || "-"}
        >
          {original?.contactInfo?.[0]?.email || "-"}
        </span>
      ),
    },
    {
      Header: "Referral Code",
      accessor: "referralCode",
      id: "referralCode",
      width: 120,
      minWidth: 110,
      maxWidth: 140,
      disableSortBy: false,
      sortType: "alphanumeric",
      headerStyle: {
        textAlign: "center",
        padding: "8px",
      },
      style: {
        textAlign: "center",
        padding: "8px",
      },
      Cell: ({ row: { original } }) => (
        <span className="text-[12px]">{original?.referralCode || "-"}</span>
      ),
    },
    {
      Header: "Action",
      accessor: "action",
      id: "action",
      width: 180,
      minWidth: 170,
      maxWidth: 220,
      disableSortBy: true,
      headerStyle: {
        textAlign: "center",
        padding: "8px",
      },
      style: {
        textAlign: "center",
        padding: "8px",
      },
      Cell: ({ row }) => (
        <div className="flex w-full items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => {
              localStorage.setItem("profilePage", "partnerView");
              navigate("/admin/hospitalProfile", {
                state: {
                  userID: row.original._id,
                },
              });
            }}
            className="inline-flex items-center justify-center hover:opacity-70 transition-opacity"
            aria-label="View Profile"
          >
            <VscEye size={18} color="blue" />
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              role="switch"
              aria-checked={row.original?.active !== false}
              aria-label={`Set partner ${row.original?.basicInfo?.name || ""} active status`}
              disabled={togglingPartnerId === row.original?._id}
              onClick={() =>
                handlePartnerActiveToggle(
                  row.original?._id,
                  row.original?.active !== false,
                )
              }
              className={`relative inline-flex h-4 w-9 items-center rounded-full transition-colors ${
                row.original?.active !== false ? "bg-green-500" : "bg-gray-400"
              } ${
                togglingPartnerId === row.original?._id
                  ? "opacity-60 cursor-not-allowed"
                  : "cursor-pointer"
              }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                  row.original?.active !== false
                    ? "translate-x-5"
                    : "translate-x-1"
                }`}
              />
            </button>

            {togglingPartnerId === row.original?._id && (
              <span className="h-3.5 w-3.5 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
            )}
          </div>
        </div>
      ),
    },
  ];

  const filteredData = useMemo(() => {
    let filtered = Array.isArray(userList) ? [...userList] : [];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((item) => {
        const name = item?.basicInfo?.name?.toLowerCase() || "";
        const phone = String(item?.contactInfo?.[0]?.phone ?? "");
        const city = item?.addressInfo?.city?.toLowerCase() || "";

        return (
          name.includes(term) || phone.includes(term) || city.includes(term)
        );
      });
    }

    if (filters.partnerType.length > 0) {
      const selectedPartnerTypes = new Set(filters.partnerType);
      filtered = filtered.filter((item) =>
        selectedPartnerTypes.has(normalizePartnerType(item?.providerType)),
      );
    }

    if (filters.city.length > 0) {
      const selectedCities = new Set(filters.city);
      filtered = filtered.filter((item) =>
        selectedCities.has(item?.addressInfo?.city?.toLowerCase?.() || ""),
      );
    }

    return filtered.reverse();
  }, [filters, userList, searchTerm]);

  useEffect(() => {
    setNoUsersFound(filteredData.length === 0);
  }, [filteredData]);

  // console.log(filteredData);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page: tablePage,
    prepareRow,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    setPageSize,
    gotoPage,
    state: { pageIndex, sortBy: currentSortBy },
  } = useTable(
    {
      columns: useMemo(() => getColumns(), [togglingPartnerId]),
      data: filteredData,
      initialState: {
        pageSize: selectedPageCount,
        pageIndex: paginationPage,
        sortBy,
      },
    },
    useSortBy,
    usePagination,
  );

  useEffect(() => {
    localStorage.setItem("partnerSortBy", JSON.stringify(currentSortBy));
  }, [currentSortBy]);

  useEffect(() => {
    if (pageIndex !== paginationPage) {
      setPaginationPage(pageIndex);
    }
  }, [pageIndex]);

  useEffect(() => {
    setPageSize(selectedPageCount);
    gotoPage(0);
  }, [selectedPageCount, setPageSize, gotoPage]);

  // Show loader while loading
  if (loading) {
    return (
      <div className="flex justify-center items-center h-[85vh] bg-[#F3F6F5]">
        <Loader loading={loading} />
      </div>
    );
  }

  return (
    <div className="bg-[#F3F6F5]">
      {selectedTab !== "performance" ? (
        <div className="overflow-x-auto bg-white rounded-2xl w-auto md:w-auto lg:w-auto shadow-lg h-[89vh] ml-2 flex flex-col gap-3">
          <div className="flex justify-between items-center mt-[-20px]">
            <div className="flex flex-row items-center mt-8 p-2">
              <p className="text-[14px] flex item-center align-center ml-2 font-bold">
                Partner List ({filteredData.length})
              </p>
            </div>

            <div className="flex flex-row items-center mr-4 mt-6 p-1">
              <div
                className="mr-4 cursor-pointer"
                title="Set the page partner count"
              >
                <div className="w-[80px]">
                  <CommonDropdown
                    options={pageCount}
                    value={selectedPageCount}
                    onChange={handleChangePageCount}
                    page={"user"}
                  />
                </div>
              </div>

              <div className="relative w-[220px] max-w-xs mr-3">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full border border-blue-600 rounded-md text-[12px] text-blue-700 px-2 py-0.5 flex justify-between items-center gap-2"
                >
                  <div className="flex items-center gap-2">
                    <MdOutlineFilterList className="h-5 w-5" />
                    Filters
                    {isFilterApplied && (
                      <span className="bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                        {Object.values(filters).flat().length}
                      </span>
                    )}
                  </div>

                  {dropdownOpen ? (
                    <MdKeyboardArrowUp className="h-5 w-5" />
                  ) : (
                    <MdKeyboardArrowDown className="h-5 w-5" />
                  )}
                </button>

                {dropdownOpen && (
                  <div className="absolute mt-2 w-full bg-white border border-blue-400 rounded-md shadow-lg z-10 p-4 space-y-4 max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
                    <div>
                      <p className="text-[12px] font-semibold text-blue-500 mb-1">
                        Partner Type
                      </p>
                      {partnerTypeOptions.map((option) => (
                        <label
                          key={option.value}
                          className="flex items-center space-x-2 text-[12px] text-gray-700 mb-1"
                        >
                          <input
                            type="checkbox"
                            checked={filters.partnerType.includes(option.value)}
                            onChange={() =>
                              toggleFilterValue("partnerType", option.value)
                            }
                          />
                          <span>
                            {option.label} ({option.count})
                          </span>
                        </label>
                      ))}
                    </div>

                    <div>
                      <p className="text-[12px] font-semibold text-blue-500 mb-1">
                        City
                      </p>
                      {cityOptions.map((option) => (
                        <label
                          key={option.value}
                          className="flex items-center space-x-2 text-[12px] text-gray-700 mb-1"
                        >
                          <input
                            type="checkbox"
                            checked={filters.city.includes(option.value)}
                            onChange={() =>
                              toggleFilterValue("city", option.value)
                            }
                          />
                          <span>
                            {option.label} ({option.count})
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div
                onClick={resetFilters}
                className={`mr-4 ${isFilterApplied ? "cursor-pointer" : "cursor-not-allowed"}`}
                title="Reset filter"
              >
                <GrPowerReset
                  color={isFilterApplied ? "blue" : "gray"}
                  size={18}
                  style={{ opacity: isFilterApplied ? 0.8 : 0.3 }}
                />
              </div>

              <div
                className="flex flex-row items-center bg-custom-gradient gap-1 p-2 rounded-md cursor-pointer"
                onClick={AddHospital}
              >
                <IoIosAddCircleOutline color="white" size={18} />
                <p className="text-[12px] text-[white]">Add Partner</p>
              </div>

              {showResetPopup && (
                <div className="absolute top-8 left-1/2 -translate-x-1/2 bg-white text-gray-800 text-sm rounded-md px-3 py-1 shadow-md border border-gray-200 z-50">
                  Filters have been reset
                </div>
              )}
            </div>
          </div>

          <div className="overflow-x-auto max-h-[75vh] mt-[-15px]">
            <div className="min-w-full w-[100%]">
              {noUsersFound ? (
                <div className="flex flex-col items-center justify-center h-[60vh]">
                  <p className="text-gray-500 text-lg">No partners found</p>
                  <p className="text-gray-400 text-sm mt-2">
                    Try adjusting your filters or search term
                  </p>
                </div>
              ) : (
                <ReusableTable
                  columns={getColumns()}
                  getTableProps={getTableProps}
                  getTableBodyProps={getTableBodyProps}
                  headerGroups={headerGroups}
                  tablePage={tablePage}
                  prepareRow={prepareRow}
                  filterIcon={CommonImages.filter}
                  searchTerm={searchTerm}
                  drop={dropdownOpen}
                />
              )}
            </div>
          </div>

          {!noUsersFound && (
            <div className="mb-2">
              <Page
                paginationPage={paginationPage}
                nextPage={nextPage}
                previousPage={previousPage}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageOptions={pageOptions}
                screen="hospital"
              />
            </div>
          )}
        </div>
      ) : (
        <Performence />
      )}
    </div>
  );
};

export default Hospital;
